/**
 * TikTokAdMockup Component
 *
 * TikTok in-feed ad mockup
 * Vertical video format (9:16)
 */

import React from 'react';
import { stripMediaTags } from '@/utils/mediaTagParser';
import { AdMockupContainer } from '../base/AdMockupContainer';
import { AdMockupMedia } from '../base/AdMockupMedia';
import { AdMockupCTA } from '../base/AdMockupCTA';
import { TikTokAdMockupProps } from '../types/platform.types';

export const TikTokAdMockup: React.FC<TikTokAdMockupProps> = ({
  videoUrl,
  caption,
  captions,
  callToAction = 'Learn More',
  brandName = 'yourbrand',
  hashtags = [],
  soundName,
}) => {
  // Support both singular and plural props for backward compatibility
  const allCaptions = captions || (caption ? [caption] : []);
  // Strip MEDIA tags from caption before display (video shown separately above)
  const displayCaption = stripMediaTags(allCaptions[0] || '');

  return (
    <AdMockupContainer platform="tiktok">
      <div className="rounded-lg border-2 border-gray-300 dark:border-border-dark overflow-hidden bg-black shadow-sm max-w-sm mx-auto">
        {/* TikTok Video Preview (9:16 aspect ratio) */}
        <div className="relative" style={{ aspectRatio: '9/16' }}>
          {videoUrl ? (
            <AdMockupMedia
              mediaUrl={videoUrl}
              aspectRatio="9:16"
              platform="tiktok"
              alt="TikTok video"
              showPlayButton={true}
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-gray-800 via-gray-900 to-black flex items-center justify-center">
              <svg className="w-20 h-20 text-gray-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
              </svg>
            </div>
          )}

          {/* Right side actions */}
          <div className="absolute right-3 bottom-24 flex flex-col gap-4 items-center">
            <div className="flex flex-col items-center gap-1">
              <div className="w-12 h-12 rounded-full bg-gray-700 flex items-center justify-center">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <span className="text-white text-xs font-semibold">12.5K</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <div className="w-12 h-12 rounded-full bg-gray-700 flex items-center justify-center">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <span className="text-white text-xs font-semibold">2,341</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <div className="w-12 h-12 rounded-full bg-gray-700 flex items-center justify-center">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
              </div>
              <span className="text-white text-xs font-semibold">8,742</span>
            </div>
          </div>

          {/* Bottom caption area */}
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center text-white text-xs font-bold">
                {brandName.slice(0, 2).toUpperCase()}
              </div>
              <span className="text-white font-semibold text-sm">@{brandName}</span>
              <span className="px-3 py-1 bg-white text-black text-xs font-bold rounded">Follow</span>
            </div>
            <p className="text-white text-sm mb-2">
              {displayCaption.length > 80 ? `${displayCaption.slice(0, 80)}...` : displayCaption}
              {hashtags.length > 0 && (
                <span className="text-blue-300">
                  {' '}{hashtags.map(tag => `#${tag}`).join(' ')}
                </span>
              )}
            </p>

            {/* Show additional caption variations */}
            {allCaptions.length > 1 && (
              <div className="mb-2 p-2 bg-black/60 rounded backdrop-blur-sm">
                <p className="text-xs font-semibold text-white/90 mb-1">
                  📝 +{allCaptions.length - 1} more caption variation{allCaptions.length > 2 ? 's' : ''}
                </p>
                {allCaptions.slice(1, 2).map((cap, idx) => (
                  <p key={idx} className="text-xs text-white/80 line-clamp-2">
                    {cap}
                  </p>
                ))}
              </div>
            )}

            {soundName && (
              <p className="text-white/70 text-xs mb-2">♫ {soundName}</p>
            )}
            <AdMockupCTA
              text={callToAction}
              platform="tiktok"
              variant="primary"
              fullWidth={true}
            />
            <p className="text-white/70 text-xs mt-2">Sponsored</p>
          </div>
        </div>
      </div>
    </AdMockupContainer>
  );
};

export default TikTokAdMockup;
